/**
 * Moderation settings (C13): the agency's word lists (versioned starting file, editable here),
 * the AI's classification and answers, and the WhatsApp invitation added to every public answer.
 */

import { type SocialSettings, useSocialSettingsList, useSocialSettingsPartialUpdate, useSocialSettingsResetTermsCreate } from "@agencyos/api-client";
import { Button, GlassCard, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { useSocialRoles } from "./shared";

interface Draft {
  insults: string;
  spam: string;
  autoHide: boolean;
  aiModeration: boolean;
  threshold: string;
  aiReplies: boolean;
  maxChars: string;
  whatsapp: string;
  inviteFr: string;
  inviteEn: string;
}

const lines = (text: string) => text.split("\n").map((line) => line.trim()).filter(Boolean);

function draftOf(row: SocialSettings): Draft {
  return {
    insults: row.insult_terms.join("\n"),
    spam: row.spam_terms.join("\n"),
    autoHide: row.auto_hide ?? true,
    aiModeration: row.ai_moderation ?? true,
    threshold: String(row.ai_hide_threshold),
    aiReplies: row.ai_replies ?? true,
    maxChars: String(row.max_public_reply_chars ?? 300),
    whatsapp: row.whatsapp_e164 ?? "",
    inviteFr: row.invite_fr ?? "",
    inviteEn: row.invite_en ?? "",
  };
}

export function ModerationTab() {
  const { t } = useTranslation();
  const { canManage } = useSocialRoles();
  const list = useSocialSettingsList();
  const row = list.data?.status === 200 ? list.data.data.results[0] : undefined;
  const save = useSocialSettingsPartialUpdate({ mutation: { onSuccess: () => void list.refetch() } });
  const reset = useSocialSettingsResetTermsCreate({ mutation: { onSuccess: () => { setDraft(null); void list.refetch(); } } });
  const [draft, setDraft] = useState<Draft | null>(null);
  const errors = fieldErrors(save.error);
  if (!row) return list.isPending ? <Skeleton style={{ height: 200 }} /> : <p role="alert">{t("common.error")}</p>;
  const form = draft ?? draftOf(row);
  const set = (patch: Partial<Draft>) => setDraft({ ...form, ...patch });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        id: row.id,
        data: {
          insult_terms: lines(form.insults),
          spam_terms: lines(form.spam),
          auto_hide: form.autoHide,
          ai_moderation: form.aiModeration,
          ai_hide_threshold: Number(form.threshold),
          ai_replies: form.aiReplies,
          max_public_reply_chars: Number.parseInt(form.maxChars, 10),
          whatsapp_e164: form.whatsapp.trim(),
          invite_fr: form.inviteFr,
          invite_en: form.inviteEn,
        },
      },
      { onSuccess: (r) => r.status === 200 && setDraft(null) },
    );
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="social-moderation-title">
      <h2 id="social-moderation-title">{t("social.moderation.title")}</h2>
      <p className="muted">{t("social.moderation.intro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("social.moderation.title")}>
        <fieldset className="plain-fieldset moderation-grid" disabled={!canManage}>
          <div className="moderation-grid__switches">
          <SoftToggle label={t("social.moderation.autoHide")} checked={form.autoHide} disabled={!canManage} onChange={(autoHide) => set({ autoHide })} />
          <SoftToggle label={t("social.moderation.aiModeration")} checked={form.aiModeration} disabled={!canManage} onChange={(aiModeration) => set({ aiModeration })} />
          <SoftToggle label={t("social.moderation.aiReplies")} checked={form.aiReplies} disabled={!canManage} onChange={(aiReplies) => set({ aiReplies })} />
          <p className="muted">{t("social.moderation.aiRepliesHint")}</p>
          </div>
          <div className="form-grid">
            <TextField type="number" min={0} max={1} step={0.05} label={t("social.moderation.threshold")} hint={t("social.moderation.thresholdHint")} value={form.threshold} error={errors.ai_hide_threshold} onChange={(e) => set({ threshold: e.target.value })} />
            <TextField type="number" min={50} max={2000} label={t("social.moderation.maxChars")} value={form.maxChars} error={errors.max_public_reply_chars} onChange={(e) => set({ maxChars: e.target.value })} />
            <TextField type="tel" label={t("social.moderation.whatsapp")} hint={row.whatsapp_link ? t("social.moderation.whatsappCurrent", { link: row.whatsapp_link }) : t("social.moderation.whatsappHint")} value={form.whatsapp} error={errors.whatsapp_e164} onChange={(e) => set({ whatsapp: e.target.value })} />
          </div>
          <div className="form-grid">
            <TextField label={t("social.moderation.inviteFr")} hint={t("social.moderation.inviteHint")} value={form.inviteFr} maxLength={200} error={errors.invite_fr} onChange={(e) => set({ inviteFr: e.target.value })} />
            <TextField label={t("social.moderation.inviteEn")} hint={t("social.moderation.inviteHint")} value={form.inviteEn} maxLength={200} error={errors.invite_en} onChange={(e) => set({ inviteEn: e.target.value })} />
          </div>
          <div className="form-grid">
            <TextAreaField label={t("social.moderation.insults")} hint={t("social.moderation.listHint")} rows={8} value={form.insults} error={errors.insult_terms} onChange={(e) => set({ insults: e.target.value })} />
            <TextAreaField label={t("social.moderation.spam")} hint={t("social.moderation.listHint")} rows={8} value={form.spam} error={errors.spam_terms} onChange={(e) => set({ spam: e.target.value })} />
          </div>
          <p className="muted">{t("social.moderation.version", { version: row.terms_version, seed: row.seed_version })}</p>
        </fieldset>
        {canManage ? (
          <div className="row">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t("common.loading") : t("common.save")}
            </Button>
            <Button variant="ghost" disabled={reset.isPending} onClick={() => reset.mutate({ id: row.id })}>
              {t("social.moderation.reset")}
            </Button>
            {save.isSuccess && !draft ? <span role="status">{t("common.saved")}</span> : null}
          </div>
        ) : (
          <p className="muted">{t("social.moderation.readOnly")}</p>
        )}
      </form>
    </GlassCard>
  );
}
